import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import classes from "../App.module.css";
import AuthPage from "../modules/authentication/screens/AuthPage";
import ResetPasswordPage from "../modules/authentication/screens/ResetPasswordPage";
import IntroPage from "../modules/questionnaire/components/pages/IntroPage";
import BiometricQuestionnaire from "../modules/questionnaire/components/pages/BiometricQuestionnaire";
import LifestyleQuestionnaire from "../modules/questionnaire/components/pages/LifestyleQuestionnaire";
import SuccessPageQuestionnaire from "../modules/questionnaire/components/pages/SuccessPageQuestionnaire";
import ErrorPage from "../modules/questionnaire/components/pages/ErrorPage";
import Report from "../modules/reports/reports/Report";
import NewReport from "../modules/new-reports/reports/NewReport";
import React from "react";
// @ts-ignore
import { Helmet } from "react-helmet";
import WorkspaceShell from "../modules/platform/ScreenWrappers/WorkspaceShell";
import AffiliateShell from "../modules/platform/ScreenWrappers/AffiliateShell";
import ClientsScreen from "../modules/platform/screens/ClientsScreen";
import ClientInfoScreen from "../modules/platform/screens/ClientInfoScreen";
import ProfileScreen from "../modules/platform/screens/ProfileScreen";
import BasicInformationTab from "../modules/platform/components/profile/BasicInformationTab";
import OnboardingTab from "../modules/platform/components/profile/OnboardingTab";
import DashboardScreen from "../modules/platform/screens/DashboardScreen";
import HealthOverviewScreen from "../modules/platform/screens/HealthOverviewScreen";
import ProgrammingScreen from "../modules/platform/screens/ProgrammingScreen";
import PricingScreen from "../modules/platform/screens/PricingScreen";
import ServicesLayout from "../modules/platform/layouts/ServicesLayout";
import ServiceBuilder from "../modules/platform/components/seviceBuilder/ServiceBuilder";
import LogicBuilder from "../modules/platform/screens/LogicBuilder";
import LogicDetailsViewWrapper from "../modules/platform/components/logicBuilder/LogicDetailsViewWrapper";
import LabBuilder from "../modules/platform/components/labBuilder/LabBuilder";
import PanelDetailsViewWrapper from "../modules/platform/components/labBuilder/PanelDetailsViewWrapper";
import HistoryScreen from "../modules/platform/screens/HistoryScreen";
import AssessmentsHistoryTab from "../modules/platform/components/history/AssessmentsHistoryTab";
import ProgrammingHistoryTab from "../modules/platform/components/history/ProgrammingHistoryTab";
import PrescriptionHistoryTab from "../modules/platform/components/history/PrescriptionHistoryTab";
import TimelineTab from "../modules/platform/components/history/TimelineTab";
import LabOrderDetailRoute from "../modules/platform/components/history/LabOrderDetailRoute";
import PnoeTestDetailRoute from "../modules/platform/components/history/PnoeTestDetailRoute";
import InbodyTestDetailRoute from "../modules/platform/components/history/InbodyTestDetailRoute";
import ServiceHistoryDetailView from "../modules/platform/components/history/ServiceHistoryDetailView";
import PrivateRoute from "./PrivateRoute";
import AuthRoute from "./AuthRoute";
import { isMobile } from "react-device-detect";
import NutritionTab from "../modules/platform/components/nutritionTab/NutritionTab";
import NutritionProgrammingTab from "../modules/platform/components/programming/NutritionProgrammingTab";
import WorkoutProgrammingTab from "../modules/platform/components/programming/WorkoutProgrammingTab";
import BioOptimizationPlanTab from "../modules/platform/components/programming/BioOptimizationPlanTab";
import SettingsScreen from "../modules/platform/screens/SettingsScreen";
import WalkthroughsScreen from "../modules/platform/screens/WalkthroughsScreen";
import WalkthroughDetailScreen from "../modules/platform/screens/WalkthroughDetailScreen";
import SharePage from "../modules/share/components/SharePage";
import ConsultationScreen from "../modules/consultation/ConsultationScreen";
import { isDemoConsultationId } from "../modules/consultation/data/demo/demoConfig";
import HrmPage from "../modules/hrm/HrmPage";
import { getHrmSmartAppBannerContent } from "../modules/hrm/hrmAppLink";

/**
 * Route entry for `/consultation`. The public demo (URL `id` === the demo
 * sentinel) renders WITHOUT auth; every other id keeps the normal
 * `PrivateRoute` gate. Keeps `PrivateRoute` generic — the demo exception lives
 * here, next to the consultation route.
 */
const ConsultationRouteEntry: React.FC = () => {
  const [searchParams] = useSearchParams();
  if (isDemoConsultationId(searchParams.get("id"))) {
    return <ConsultationScreen />;
  }
  return (
    <PrivateRoute>
      <ConsultationScreen />
    </PrivateRoute>
  );
};

/**
 * Redirect that keeps the query string. Works for relative targets (index
 * routes) and absolute ones (legacy aliases).
 *
 * The client workspace identifies its subject with `?id=`, so a bare
 * `<Navigate to="…">` silently drops the client and the page reports "No client
 * selected". Chat-mode params ride along too.
 */
const RedirectWithQuery = ({ to }: { to: string }) => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
};

/**
 * `/client/orders/:orderId` → the same order under History → Assessments.
 * `RedirectWithQuery` takes a literal target, so the param is read here.
 */
const LegacyOrderRedirect = () => {
  const { orderId } = useParams();
  return (
    <RedirectWithQuery to={`/client/history-list/assessments/lab/${orderId}`} />
  );
};

/** `/client/client-info/prescription/:id` → the History detail route. */
const LegacyPrescriptionRedirect = () => {
  const { prescriptionId } = useParams();
  return (
    <RedirectWithQuery
      to={`/client/history/prescription/${prescriptionId}`}
    />
  );
};

/** `/client/orders/pnoe-test/:measurementId` → the History detail route. */
const LegacyPnoeTestRedirect = () => {
  const { measurementId } = useParams();
  return (
    <RedirectWithQuery
      to={`/client/history-list/assessments/pnoe/${measurementId}`}
    />
  );
};

/**
 * The History page's child routes.
 *
 * The same three tabs in all three shells, plus Timeline wherever there is a
 * single subject to chart — so the tab strip (`HistoryScreen`, via
 * `useHistoryScope`) and the route tree agree on where it exists. Detail routes
 * are nested under their tab segment so the tab stays addressable and active
 * while a record is open.
 */
const historyRouteChildren = ({
  withTimeline = false,
}: { withTimeline?: boolean } = {}) => [
  { index: true, element: <RedirectWithQuery to="assessments" /> },
  { path: "assessments", element: <AssessmentsHistoryTab /> },
  { path: "assessments/lab/:orderId", element: <LabOrderDetailRoute /> },
  {
    path: "assessments/pnoe/:measurementId",
    element: <PnoeTestDetailRoute />,
  },
  { path: "assessments/inbody/:recordId", element: <InbodyTestDetailRoute /> },
  { path: "programming", element: <ProgrammingHistoryTab /> },
  { path: "programming/:serviceId", element: <ServiceHistoryDetailView /> },
  { path: "prescription", element: <PrescriptionHistoryTab /> },
  {
    path: "prescription/:prescriptionId",
    element: <PrescriptionHistoryTab />,
  },
  ...(withTimeline ? [{ path: "timeline", element: <TimelineTab /> }] : []),
];

const router = createBrowserRouter([
  {
    path: "/auth",
    element: (
      <AuthRoute>
        <Helmet>
          <title>PNOĒ - Login</title>
        </Helmet>
        <AuthPage />
      </AuthRoute>
    ),
  },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  {
    path: "/intro",
    element: (
      <PrivateRoute>
        <IntroPage />
      </PrivateRoute>
    ),
  },
  {
    path: "/biometric-questionnaire",
    element: (
      <PrivateRoute>
        <BiometricQuestionnaire />
      </PrivateRoute>
    ),
  },
  {
    path: "/lifestyle-questionnaire",
    element: (
      <PrivateRoute>
        <Helmet>
          <title>PNOĒ - Lifestyle Questionnaire</title>
        </Helmet>
        <LifestyleQuestionnaire />
      </PrivateRoute>
    ),
  },
  {
    path: "/success-biometric",
    element: (
      <PrivateRoute>
        <SuccessPageQuestionnaire />
      </PrivateRoute>
    ),
  },
  {
    path: "/success-lifestyle",
    element: (
      <PrivateRoute>
        <SuccessPageQuestionnaire />
      </PrivateRoute>
    ),
  },
  {
    path: "/error",
    element: (
      <PrivateRoute>
        <ErrorPage />
      </PrivateRoute>
    ),
  },
  {
    path: "/reports",
    element: (
      <>
        <Helmet>
          <title>PNOĒ - Report</title>
        </Helmet>
        <Report />
      </>
    ),
  },
  {
    path: "/new-reports/:id",
    element: (
      <>
        <Helmet>
          <title>PNOĒ - Services Report</title>
        </Helmet>
        <NewReport />
      </>
    ),
  },
  {
    // The customer's own workspace (role 1): the same single-subject shell as
    // the affiliate's client tab, with the subject resolved from the login.
    path: "/platform/",
    element: (
      <PrivateRoute>
        <Helmet>
          <title>PNOĒ - Dashboard</title>
        </Helmet>
        <WorkspaceShell mode="self" />
      </PrivateRoute>
    ),
    children: [
      { path: "dashboard", element: <DashboardScreen /> },
      {
        // The customer's own Profile — same screen as the client workspace's,
        // with the subject resolved from the login by SubjectProvider.
        path: "profile",
        element: <ProfileScreen />,
        children: [
          { index: true, element: <RedirectWithQuery to="basic-information" /> },
          { path: "basic-information", element: <BasicInformationTab /> },
          { path: "onboarding", element: <OnboardingTab /> },
          { path: "nutrition-questionnaire", element: <NutritionTab /> },
        ],
      },
      { path: "score", element: <HealthOverviewScreen /> },
      {
        path: "programming",
        element: <ProgrammingScreen />,
        children: [
          { index: true, element: <RedirectWithQuery to="nutrition" /> },
          { path: "nutrition", element: <NutritionProgrammingTab /> },
          { path: "workout", element: <WorkoutProgrammingTab /> },
          { path: "bio-optimization", element: <BioOptimizationPlanTab /> },
        ],
      },
      {
        path: "history",
        element: <HistoryScreen />,
        children: historyRouteChildren({ withTimeline: true }),
      },
      // Absolute target: with `v7_relativeSplatPath` a relative "dashboard"
      // resolves against the full splat match, so `/platform/onboarding` became
      // `/platform/onboarding/dashboard`, matched `*` again, and looped forever
      // into a blank page.
      { path: "*", element: <RedirectWithQuery to="/platform/dashboard" /> },
    ],
  },
  {
    path: "/client/",
    children: [
      {
        // Affiliate main tab: client management, no per-client context.
        element: (
          <PrivateRoute>
            <Helmet>
              <title>PNOĒ - Dashboard</title>
            </Helmet>
            <AffiliateShell />
          </PrivateRoute>
        ),
        children: [
          { path: "client-list", element: <ClientsScreen /> },
          // Orders became History → Assessments. Keep the old URLs working:
          // they were the affiliate's day-to-day bookmarks, and the two detail
          // routes are linked from emails. `pnoe-test` is a static segment and
          // outranks the dynamic `:orderId`, so they never collide.
          {
            path: "orders",
            element: <RedirectWithQuery to="/client/history-list/assessments" />,
          },
          { path: "orders/:orderId", element: <LegacyOrderRedirect /> },
          {
            path: "orders/pnoe-test/:measurementId",
            element: <LegacyPnoeTestRedirect />,
          },
          {
            path: "history-list",
            element: <HistoryScreen />,
            children: historyRouteChildren(),
          },
          { path: "settings", element: <SettingsScreen /> },
          { path: "walkthroughs", element: <WalkthroughsScreen /> },
          { path: "walkthroughs/:id", element: <WalkthroughDetailScreen /> },
          {
            path: "services",
            element: <ServicesLayout />,
            children: [
              { index: true, element: <ServiceBuilder /> },
              { path: "logic", element: <LogicBuilder /> },
              { path: "logic/:logicId", element: <LogicDetailsViewWrapper /> },
              { path: "lab", element: <LabBuilder /> },
              { path: "lab/:panelId", element: <PanelDetailsViewWrapper /> },
              // {
              //   path: "pricing/*",
              //   element: <PricingScreen />,
              // },
            ],
          },
          { path: "*", element: <ClientsScreen /> },
        ],
      },
      {
        // Client workspace: one client per browser tab, resolved from `?id=`.
        element: (
          <PrivateRoute>
            <Helmet>
              <title>PNOĒ - Dashboard</title>
            </Helmet>
            <WorkspaceShell mode="client" />
          </PrivateRoute>
        ),
        children: [
          {
            // Profile: the client's own record. Biometrics and Nutrition moved
            // here out of client-info, joined by the new Onboarding tab.
            path: "profile",
            element: <ProfileScreen />,
            children: [
              { index: true, element: <RedirectWithQuery to="basic-information" /> },
              { path: "basic-information", element: <BasicInformationTab /> },
              { path: "onboarding", element: <OnboardingTab /> },
              { path: "nutrition-questionnaire", element: <NutritionTab /> },
            ],
          },
          {
            path: "client-info",
            element: <ClientInfoScreen />,
            children: [
              { index: true, element: <RedirectWithQuery to="/client/history/prescription" /> },
              {
                // Prescription moved into History. These were the affiliate's
                // bookmarks for a client's prescriptions, so keep them working.
                path: "prescription",
                children: [
                  {
                    index: true,
                    element: (
                      <RedirectWithQuery to="/client/history/prescription" />
                    ),
                  },
                  {
                    path: ":prescriptionId",
                    element: <LegacyPrescriptionRedirect />,
                  },
                ],
              },
              // Legacy aliases — Biometrics and Nutrition moved to Profile,
              // and the Biomarkers tab was absorbed by the Health overview.
              // Sending them to where the content actually lives beats a
              // generic bounce, and being static segments they outrank any
              // splat.
              {
                path: "biomarkers",
                element: <RedirectWithQuery to="/client/score" />,
              },
              {
                path: "biometrics",
                element: (
                  <RedirectWithQuery to="/client/profile/basic-information" />
                ),
              },
              {
                path: "nutrition",
                element: (
                  <RedirectWithQuery to="/client/profile/nutrition-questionnaire" />
                ),
              },
              // Anything else under client-info: stay in the workspace. Without
              // this the bare `*` in the AffiliateShell branch below claims the
              // URL, which renders the client list AND clears the tab's client
              // session on mount — losing the client instead of redirecting it.
              { path: "*", element: <RedirectWithQuery to="/client/dashboard" /> },
            ],
          },
          { path: "dashboard", element: <DashboardScreen /> },
          { path: "score", element: <HealthOverviewScreen /> },
          {
            path: "history",
            element: <HistoryScreen />,
            children: historyRouteChildren({ withTimeline: true }),
          },
          {
            path: "programming",
            element: <ProgrammingScreen />,
            children: [
              { index: true, element: <RedirectWithQuery to="nutrition" /> },
              { path: "nutrition", element: <NutritionProgrammingTab /> },
              { path: "workout", element: <WorkoutProgrammingTab /> },
              { path: "bio-optimization", element: <BioOptimizationPlanTab /> },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/hrm",
    element: (
      <>
        <Helmet>
          <title>PNOĒ - Heart Rate Monitor</title>
          <meta
            name="description"
            content="The PNOĒ Heart Rate Monitor is designed for precise metabolic assessments and exercise testing."
          />
          <meta
            name="apple-itunes-app"
            content={getHrmSmartAppBannerContent(window.location.hostname)}
          />
        </Helmet>
        <HrmPage />
      </>
    ),
  },
  {
    path: "/share",
    element: (
      <>
        <Helmet>
          <title>PNOĒ - Share</title>
        </Helmet>
        <SharePage />
      </>
    ),
  },
  {
    path: "/consultation",
    element: <ConsultationRouteEntry />,
  },
  {
    path: "*",
    element: <Navigate to="/auth" replace />,
  },
], {
  future: {
    v7_relativeSplatPath: true,
  },
});

const RootNavigation = () => {
  const appWidth = getComputedStyle(document.documentElement).getPropertyValue(
    "--app-width"
  );
  return (
    <div
      className={classes.app}
      style={{ width: appWidth, height: isMobile ? "100%" : "100vh" }}
    >
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </div>
  );
};

export default RootNavigation;
